import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { useState } from 'react';
import { deriveBillMode } from '~/components/BillModal/components/BillingDate/BillingDate.utils';
import { useCreateServiceTemplateForBill } from '~/components/BillModal/hooks/useCreateServiceTemplateForBill';
import { appendItemForSelection, billingStagesTextsPath, createEmptyChargeItem, isItemUnselected, overrideItemForSelection, seedChargeItemFromTemplate } from '~/components/BillModal/utils/lineItem.utils';
import { useStore, useStoreActions } from '~/contexts';
import { useNonNullableRelationship } from '~/hooks/reactQuery/queries/useRelationship';
import { useServiceTemplates } from '~/hooks/reactQuery/queries/useServiceTemplates';
import { useAttachFiles } from '~/hooks/useAttachFiles/useAttachFiles';
import { useLocaleKeys } from '~/i18n';
import { serviceCostModel } from '~/models';
import { costDisplayTextsPath } from '~/models/service-cost/selectors';
import { ServiceTemplateType } from '~/models/service-template';
import { selectors } from '~/store/state-selectors';
import { billingPeriodTextsPath } from '~/utils/billing-period-text';
import { useAgreementServiceOptions } from '../../hooks/useAgreementServiceOptions';
import { useBillClientSubmit } from '../../hooks/useBillClientSubmit';
import { useBillItems } from '../../hooks/useBillItems';
import { useTimeActivitiesController } from './useTimeActivitiesController';
const EMPTY_SERVICE_TEMPLATES = [];
export const useBillClientBody = (param)=>{
    let { props, form, dateContext } = param;
    var _ref, _ref1;
    var _dateContext_invoiceDateInfo;
    const { relationship, optimisticUpdate } = useNonNullableRelationship(props.relationshipId);
    const { logBI } = useStoreActions();
    const descriptionEnabled = useStore(selectors.user.invoiceLineItemDescriptionEnabled);
    const { t: costDisplayTexts } = useLocaleKeys(costDisplayTextsPath);
    const { t: billingTexts } = useLocaleKeys(billingPeriodTextsPath);
    const { t: stagesTexts } = useLocaleKeys(billingStagesTextsPath);
    const billItems = useBillItems();
    const { appendItem, replaceItem } = billItems;
    const [issueDate, items] = form.watch([
        'issueDate',
        'items'
    ]);
    const { attachments, openFilePicker, isUploading, handleRemove } = useAttachFiles();
    const mode = deriveBillMode({
        date: issueDate,
        invoiceDateInfo: dateContext.invoiceDateInfo,
        earliestBillableDate: dateContext.earliestBillableDate,
        now: dateContext.now
    });
    const timeActivities = useTimeActivitiesController({
        relationship,
        controlData: props.controlData,
        defaultItem: props.defaultItem,
        billItems,
        items,
        setRelationship: optimisticUpdate
    });
    const agreementOptions = useAgreementServiceOptions({
        services: relationship.services,
        controlData: props.controlData,
        items
    });
    const { data: serviceTemplatesData } = useServiceTemplates(ServiceTemplateType.Service, {
        refreshOnHookMount: true
    });
    const outOfScopeTemplates = (_ref = serviceTemplatesData === null || serviceTemplatesData === void 0 ? void 0 : serviceTemplatesData.active) !== null && _ref !== void 0 ? _ref : EMPTY_SERVICE_TEMPLATES;
    const { openCreateServiceTemplate } = useCreateServiceTemplateForBill({
        relationship
    });
    const [showUnselectedItemError, setShowUnselectedItemError] = useState(false);
    const { onSubmit } = useBillClientSubmit({
        form,
        mode,
        relationshipId: relationship.id,
        now: dateContext.now,
        dismissModal: props.dismissModal,
        isUploading,
        attachments,
        timeActivities: timeActivities.timeActivities,
        allowTimeActivities: timeActivities.allowTimeActivities
    });
    const formatTemplateCost = (template)=>serviceCostModel.formattedCost(template.cost, costDisplayTexts, billingTexts.periods);
    const onCreateNewCharge = async ()=>{
        const template = await openCreateServiceTemplate();
        if (template == null) return;
        logBI(18407);
        appendItem(seedChargeItemFromTemplate(template, {
            descriptionEnabled
        }));
    };
    const onAddCharge = (selection)=>{
        logBI(18406, {
            relationshipId: relationship.id
        });
        appendItem(appendItemForSelection({
            selection,
            services: relationship.services,
            controlData: props.controlData,
            t: stagesTexts
        }));
    };
    const onSelectItem = (rowIndex, selection)=>{
        const existingItem = form.getValues("items.".concat(rowIndex));
        logBI(18406, {
            relationshipId: relationship.id
        });
        replaceItem(rowIndex, overrideItemForSelection({
            existingItemId: existingItem.itemId,
            selection,
            services: relationship.services,
            controlData: props.controlData,
            t: stagesTexts,
            descriptionEnabled
        }));
    };
    const onClearItem = (rowIndex)=>{
        replaceItem(rowIndex, createEmptyChargeItem());
    };
    const onCreateNewItem = async (rowIndex)=>{
        const existingItem = form.getValues("items.".concat(rowIndex));
        const template = await openCreateServiceTemplate();
        if (template == null) return;
        logBI(18407);
        replaceItem(rowIndex, _object_spread_props(_object_spread({}, seedChargeItemFromTemplate(template, {
            descriptionEnabled
        })), {
            itemId: existingItem.itemId
        }));
    };
    const onClose = ()=>{
        logBI(18404);
        props.dismissModal();
    };
    const submitWithItemGuard = async (data)=>{
        if (data.items.some(isItemUnselected)) return;
        await onSubmit(data);
    };
    const onSubmitClick = ()=>{
        setShowUnselectedItemError(form.getValues('items').some(isItemUnselected));
        return form.handleSubmit(submitWithItemGuard)();
    };
    return {
        form,
        mode,
        items,
        customerName: relationship.customer.name,
        clientId: relationship.customer.id,
        agreementOptions,
        outOfScopeTemplates,
        formatTemplateCost,
        invoiceDraftDates: (_ref1 = (_dateContext_invoiceDateInfo = dateContext.invoiceDateInfo) === null || _dateContext_invoiceDateInfo === void 0 ? void 0 : _dateContext_invoiceDateInfo.invoiceDraftDates.dates()) !== null && _ref1 !== void 0 ? _ref1 : [],
        invoiceDateInfo: dateContext.invoiceDateInfo,
        issueDate,
        effectiveDate: dateContext.effectiveDate,
        totalCredits: props.totalCredits,
        attachments: {
            files: attachments,
            openFilePicker,
            isUploading,
            onRemove: handleRemove
        },
        timeActivities,
        onCreateNewCharge,
        onAddCharge,
        onSelectItem,
        onClearItem,
        onCreateNewItem,
        onClose,
        onSubmitClick,
        showUnselectedItemError
    };
};
